import type { AuthContextType } from '../features/auth/types';

/**
 * Global auth service for accessing authentication methods outside of React components
 * This is particularly useful for API error handling where we need to attempt
 * token refresh when authentication fails.
 */
class AuthService {
  private authContext: AuthContextType | null = null;

  /**
   * Register the auth context
   * This should be called from the AuthProvider component
   */
  setAuthContext(authContext: AuthContextType): void {
    this.authContext = authContext;
  }

  /**
   * Attempt to refresh the session
   * @returns Promise that resolves when refresh is complete
   * @throws Error if refresh fails or auth context is not available
   */
  async refreshSession(): Promise<void> {
    if (!this.authContext) {
      throw new Error('Auth context not available');
    }

    return this.authContext.refreshSession();
  }

  /**
   * Get the current session state
   * @returns Current session or null if auth context is not available
   */
  getCurrentSession() {
    return this.authContext?.session || null;
  }

  /**
   * Check if user is currently authenticated
   * @returns true if user is authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return this.authContext?.session.isAuthenticated || false;
  }

  /**
   * Check if user has a refresh token available
   * @returns true if refresh token is available, false otherwise
   */
  hasRefreshToken(): boolean {
    return this.authContext?.session.hasRefreshToken || false;
  }

  /**
   * Perform logout
   * @returns Promise that resolves when logout is complete
   */
  async logout(): Promise<void> {
    if (!this.authContext) {
      throw new Error('Auth context not available');
    }

    return this.authContext.logout();
  }

  /**
   * Check if auth service is ready
   */
  isReady(): boolean {
    return this.authContext !== null;
  }
}

// Export singleton instance
export const authService = new AuthService(); 